import { DEADLINE_PASSED_ERROR } from "./claimFormGate.js";

const LOCAL_HOSTNAME = "local.fpasettlement.com";

/**
 * Synthetic deadline (9999-01-01 06:59:59 UTC) substituted on the exact local
 * host only. Temporary testing exception: the shared dev /claims/deadline
 * route invokes the production Lambda alias and reports the deadline passed.
 */
export const LOCAL_DEADLINE_FALLBACK = Object.freeze({
    claim_deadline: "9999-01-01T06:59:59.000Z",
    utc_deadline: "9999-01-01T06:59:59.000Z",
    pacific_datetime: "December 31, 9998 at 10:59:59 P.M.",
    eastern_datetime: "January 1, 9999 at 01:59:59 A.M.",
});

/**
 * Replace a deadline-passed response with the synthetic local deadline.
 * Every other host, success, or error is returned unchanged.
 *
 * @param {Object} result - claimsDeadlineGet response.
 * @param {string} [hostname] - Defaults to window.location.hostname.
 * @returns {Object} The original response, or a copy carrying the fallback data.
 */
export function applyLocalDeadlineFallback(
    result,
    hostname = window.location.hostname
) {
    if (hostname !== LOCAL_HOSTNAME) return result;
    if (result?.data?.error !== DEADLINE_PASSED_ERROR) return result;
    return { ...result, data: { ...LOCAL_DEADLINE_FALLBACK } };
}

/**
 * Derive an API client whose claimsDeadlineGet passes its response through
 * applyLocalDeadlineFallback. All other methods resolve to the original client
 * via the prototype chain; the original client is not mutated.
 *
 * @param {Object} api - apigClient instance.
 * @returns {Object} Adapted client.
 */
export function withLocalDeadlineFallback(api) {
    const adapted = Object.create(api);
    adapted.claimsDeadlineGet = (...args) =>
        api
            .claimsDeadlineGet(...args)
            .then((result) => applyLocalDeadlineFallback(result));
    return adapted;
}
